import { resolveApiUrl } from "./apiUrl";
import { clientFingerprintHeaders } from "./clientFingerprint";
import { nativeAppRequestHeaders } from "./nativeApp";
import { shouldUseMobileExperience } from "./device";
import type { MemberLoginResult } from "@/api/member";

export interface TelegramLoginLibraryResult {
  id_token?: string;
  user?: {
    name?: string;
    preferred_username?: string;
    picture?: string;
    phone_number?: string;
  };
  error?: string;
}

export type TelegramLoginResponse = MemberLoginResult;

const TELEGRAM_CLIENT_ID = "8890408222";
const TELEGRAM_LOGIN_SRC = "https://telegram.org/js/telegram-login.js";
const TELEGRAM_OIDC_STATE_KEY = "cinema:telegram-oidc-state";
const TELEGRAM_OIDC_VERIFIER_KEY = "cinema:telegram-oidc-verifier";
const TELEGRAM_OIDC_SHARE_CODE_KEY = "cinema:telegram-oidc-share-code";

function encodeBase64URL(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function randomBase64URL(length = 32): string {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return encodeBase64URL(bytes);
}

async function sha256Base64URL(value: string): Promise<string> {
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return encodeBase64URL(new Uint8Array(digest));
}

async function redirectToTelegramOIDC(shareCode?: string): Promise<never> {
  const state = randomBase64URL(24);
  const verifier = randomBase64URL(48);
  const redirectURI = `${window.location.origin}/`;
  window.sessionStorage.setItem(TELEGRAM_OIDC_STATE_KEY, state);
  window.sessionStorage.setItem(TELEGRAM_OIDC_VERIFIER_KEY, verifier);
  window.sessionStorage.setItem(TELEGRAM_OIDC_SHARE_CODE_KEY, shareCode || "");

  const params = new URLSearchParams({
    client_id: TELEGRAM_CLIENT_ID,
    redirect_uri: redirectURI,
    response_type: "code",
    scope: "openid profile",
    state,
    code_challenge: await sha256Base64URL(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`https://oauth.telegram.org/auth?${params.toString()}`);
  return new Promise<never>(() => undefined);
}

export function getTelegramClientId(): string {
  return TELEGRAM_CLIENT_ID;
}

export function getTelegramRedirectUri(): string {
  if (typeof window === "undefined") {
    return "";
  }
  return `${window.location.origin}${window.location.pathname}`;
}

declare global {
  interface TelegramLoginSDK {
    Login?: {
      auth: (
        options: {
          client_id: number;
          request_access?: Array<"phone" | "write">;
          lang?: string;
          nonce?: string;
        },
        callback: (result: TelegramLoginLibraryResult) => void
      ) => void;
    };
  }

  interface Window {
    Telegram?: TelegramLoginSDK;
    onTelegramOIDCAuth?: (result: TelegramLoginLibraryResult) => void;
  }
}

let telegramScriptPromise: Promise<void> | null = null;

export async function ensureTelegramLoginSDK(): Promise<void> {
  if (window.Telegram?.Login?.auth) {
    return;
  }

  if (telegramScriptPromise) {
    return telegramScriptPromise;
  }

  telegramScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TELEGRAM_LOGIN_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Telegram login unavailable")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = TELEGRAM_LOGIN_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Telegram login unavailable"));
    document.head.appendChild(script);
  }).finally(() => {
    if (!window.Telegram?.Login?.auth) {
      telegramScriptPromise = null;
    }
  });

  return telegramScriptPromise;
}

export async function signInWithTelegram(shareCode?: string): Promise<TelegramLoginLibraryResult> {
  if (shouldUseMobileExperience()) {
    return redirectToTelegramOIDC(shareCode);
  }

  await ensureTelegramLoginSDK();

  if (!window.Telegram?.Login?.auth) {
    throw new Error("Telegram login unavailable");
  }

  return new Promise<TelegramLoginLibraryResult>((resolve) => {
    window.Telegram!.Login!.auth(
      {
        client_id: Number(TELEGRAM_CLIENT_ID),
        request_access: [],
        lang: "zh-cn",
      },
      (result) => resolve(result)
    );
  });
}

export async function loginWithTelegramIDToken(idToken: string, shareCode?: string): Promise<TelegramLoginResponse> {
	const identityHeaders = await clientFingerprintHeaders();
  const response = await fetch(resolveApiUrl("/api/member/telegram/login"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...nativeAppRequestHeaders(),
			...identityHeaders,
    },
    body: JSON.stringify({ id_token: idToken, shareCode: shareCode || "" }),
  });

  const payload = (await response.json()) as {
    code: number;
    message: string;
    data: TelegramLoginResponse;
  };

  if (!response.ok || payload.code !== 0 || !payload.data) {
    throw new Error(payload.message || "Telegram login failed");
  }

  return payload.data;
}

export function hasTelegramOIDCCallback(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get("code") && params.get("state") && window.sessionStorage.getItem(TELEGRAM_OIDC_VERIFIER_KEY));
}

export async function consumeTelegramOIDCCallback(): Promise<TelegramLoginResponse | null> {
  if (!hasTelegramOIDCCallback()) return null;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code") || "";
  const returnedState = params.get("state") || "";
  const expectedState = window.sessionStorage.getItem(TELEGRAM_OIDC_STATE_KEY) || "";
  const verifier = window.sessionStorage.getItem(TELEGRAM_OIDC_VERIFIER_KEY) || "";
  const shareCode = window.sessionStorage.getItem(TELEGRAM_OIDC_SHARE_CODE_KEY) || "";
  if (!expectedState || returnedState !== expectedState) {
    throw new Error("Telegram 登录状态校验失败，请重试");
  }

  try {
		const identityHeaders = await clientFingerprintHeaders();
    const response = await fetch(resolveApiUrl("/api/member/telegram/oidc/login"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...nativeAppRequestHeaders(),
				...identityHeaders,
      },
      body: JSON.stringify({
        code,
        code_verifier: verifier,
        redirect_uri: `${window.location.origin}/`,
        shareCode,
      }),
    });
    const payload = (await response.json()) as { code: number; message: string; data: TelegramLoginResponse };
    if (!response.ok || payload.code !== 0 || !payload.data) {
      throw new Error(payload.message || "Telegram 登录失败");
    }
    return payload.data;
  } finally {
    window.sessionStorage.removeItem(TELEGRAM_OIDC_STATE_KEY);
    window.sessionStorage.removeItem(TELEGRAM_OIDC_VERIFIER_KEY);
    window.sessionStorage.removeItem(TELEGRAM_OIDC_SHARE_CODE_KEY);
  }
}
